import React, { useMemo } from 'react'
import JoditEditor from 'jodit-react'
import {
  Braces, ChevronDown, MailX,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const KNOWN_MERGE_TAGS = [
  { tag: 'first_name', label: 'First Name' },
  { tag: 'last_name', label: 'Last Name' },
  { tag: 'email', label: 'Email' },
  { tag: 'phone', label: 'Phone' },
  { tag: 'company_name', label: 'Company' },
  { tag: 'job_title', label: 'Job Title' },
  { tag: 'membership_tier', label: 'Membership Tier' },
  { tag: 'country', label: 'Country' },
]

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const MergeTagPopover = ({ onInsert, customFieldDefinitions = [] }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        title="Insert merge tag"
        className="inline-flex items-center gap-1 rounded h-8 px-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors border"
      >
        <Braces className="h-4 w-4" />
        <ChevronDown className="h-3 w-3" />
        <span>Merge tag</span>
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-56 p-1" align="start">
      <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Contact fields</p>
      {KNOWN_MERGE_TAGS.map(({ tag, label }) => (
        <button
          key={tag}
          type="button"
          className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent"
          onClick={() => onInsert(`{{${tag}}}`)}
        >
          {label}
        </button>
      ))}
      {customFieldDefinitions.length > 0 && (
        <>
          <p className="text-xs font-medium text-muted-foreground px-2 py-1.5 mt-1">Custom fields</p>
          {customFieldDefinitions.map((f) => (
            <button
              key={f.name}
              type="button"
              className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent"
              onClick={() => onInsert(`{{${f.name}}}`)}
            >
              {f.label}
            </button>
          ))}
        </>
      )}
    </PopoverContent>
  </Popover>
)

const EmailEditor = ({ content = '', onChange, placeholder = 'Write your email...', onImageUpload, customFieldDefinitions = [] }) => {
  const editorRef = React.useRef(null)
  const lastKnownContent = React.useRef(content)
  const [editorValue, setEditorValue] = React.useState(content)

  // Only push the parent's `content` into Jodit when it changes externally
  // (e.g. a different template loads). Feeding every keystroke's value back
  // into Jodit's `value` prop resets its DOM and steals focus/cursor position.
  React.useEffect(() => {
    if (content !== lastKnownContent.current) {
      lastKnownContent.current = content
      setEditorValue(content)
    }
  }, [content])

  const handleChange = (html) => {
    lastKnownContent.current = html
    onChange?.(html)
  }

  const insertHtml = (html) => {
    const jodit = editorRef.current
    if (!jodit) return
    jodit.s.insertHTML(html)
    handleChange(jodit.value)
  }

  const config = useMemo(() => ({
    readonly: false,
    placeholder,
    toolbarAdaptive: false,
    toolbarSticky: false,
    addNewLine: false,
    addNewLineOnDBLClick: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    height: 400,
    minHeight: 300,
    allowResizeTags: new Set(),
    buttons: [
      'undo', 'redo', '|',
      'bold', 'italic', 'underline', 'eraser', '|',
      'fontsize', 'brush', '|',
      'left', 'center', 'right', 'justify', '|',
      'ul', 'ol', '|',
      'table', 'link', 'image', '|',
      'source',
    ],
    cleanHTML: {
      collapseEmptyValueToEmptyString: true,
      removeEventAttributes: true,
      safeJavaScriptLink: true,
      denyTags: 'script',
    },
    uploader: {
      insertImageAsBase64URI: !onImageUpload,
      imagesExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
      customUploadFunction: onImageUpload
        ? async (requestData) => {
            const file = Array.from(requestData.values()).find((v) => v instanceof File)
            if (!file) {
              return { success: false, time: String(Date.now()), data: { files: [], baseurl: '' } }
            }
            const base64 = await readFileAsDataURL(file)
            let url = base64
            try {
              const uploaded = await onImageUpload(file)
              if (uploaded) url = uploaded
            } catch (err) {
              console.error('Image upload failed, keeping local preview:', err)
            }
            return {
              success: true,
              time: String(Date.now()),
              data: { files: [url], baseurl: '', isImages: [true] },
            }
          }
        : undefined,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [placeholder, onImageUpload])

  return (
    <div className="border rounded-lg overflow-hidden bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
      <div className="flex items-center gap-1 border-b px-1.5 py-1 flex-wrap bg-muted/30">
        <MergeTagPopover onInsert={insertHtml} customFieldDefinitions={customFieldDefinitions} />
        <button
          type="button"
          title="Insert unsubscribe link"
          className="inline-flex items-center gap-1 rounded h-8 px-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors border"
          onClick={() => insertHtml('<a href="{{unsubscribe_url}}">Unsubscribe</a>')}
        >
          <MailX className="h-4 w-4" />
          <span>Unsubscribe link</span>
        </button>
      </div>
      <JoditEditor
        ref={editorRef}
        value={editorValue}
        config={config}
        onChange={handleChange}
      />
    </div>
  )
}

export default EmailEditor
